/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { VisualizedCluResult } from 'src/clu/appShell/cluTestModelsPage/VisualizedCluResult';
import { CluTestModelResults } from 'src/clu/core/services/test/types';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    title: { id: 'clu.cluTestModelPage.title', defaultMessage: 'Testing deployments' },
    subtitle: {
        id: 'clu.cluTestModelPage.subtitle',
        defaultMessage:
            'Test a deployment by providing a sample utterance to find out what intent and entities get recognized. These are the same predictions as when making API calls against your model in code.'
    },
    learnMore: {
        id: 'clu.cluTestModelPage.learnMore',
        defaultMessage: 'Learn about the info in these API requests and responses.'
    },
    runTestButton: { id: 'clu.cluTestModelPage.runTestButton', defaultMessage: 'Run the test' },
    deploymentNameLabel: { id: 'clu.cluTestModelPage.deploymentNameLabel', defaultMessage: 'Deployment name' },
    resultsPlaceholder: { id: 'clu.cluTestModelPage.resultsPlaceholder', defaultMessage: 'Run a test to see results.' },
    hideCardsMessage: { id: 'clu.cluTestModelPage.hideCardsMessage', defaultMessage: 'Turn on cards to see entities cards' },
    deploymentNamePlaceholder: { id: 'clu.cluTestModelPage.deploymentNamePlaceholder', defaultMessage: 'Select a deployment' },
    selectDocumentTitle: {
        id: 'clu.cluTestModelPage.selectDocumentTitle',
        defaultMessage: 'Enter your own text, or upload a text document'
    }
});

const pageTheme = getScopedTheme('CustomTextTestModelPage');
const resultsContainerStyles = classNamesFunction<IStackProps, IStackStyles>()({ root: { padding: '0 !important', height: '100%' } });
const deploymentsDropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({
    dropdown: { minWidth: '300px' },
    root: { alignSelf: 'flex-start' }
});
const commandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
    root: { borderBottom: `1px solid ${pageTheme.topBorderColor}` }
});

export const CluTestModelPage = observer(() => {
    const tracker = useProgressTracker();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const { currentCluProjectStore } = rootStore;
    const {
        culture,
        projectType,
        isMultiLingual,
        name: projectName,
        id: currentProjectId,
        deploymentsStoreHouse,
        trainedModelStoreHouse
    } = currentCluProjectStore;

    const { serviceName } = getPortalFromUrl();
    const [fileText, setFileText] = React.useState('');
    const [testedDocument, setTestedDocument] = React.useState('');
    const [selectedCulture, setSelectedCulture] = React.useState<string>(culture);
    const [selectedDeploymentName, setSelectedDeploymentName] = React.useState('');
    const [testResults, setTestResults] = React.useState<CluTestModelResults>();

    React.useEffect(() => {
        if (!deploymentsStoreHouse.tracker.isCompleted) {
            dispatcher.dispatch('getCluDeployments', { deploymentsStoreHouse, projectName, trainedModelStoreHouse });
        }
    }, [deploymentsStoreHouse.tracker.isCompleted]);

    const onTestModel = () => {
        logButtonClick(serviceName, 'Testing', 'RunTest', null, currentProjectId);
        dispatcher.dispatch('testCluModel', {
            tracker,
            id: getId(),
            toastData: {},
            testQuery: fileText,
            culture: selectedCulture,
            deploymentName: selectedDeploymentName,
            setTestResults
        });

        setTestedDocument(fileText);
        const resultsStep = document.querySelector('#demoPageResultsStep');
        resultsStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'run',
            onClick: onTestModel,
            iconProps: { iconName: 'TestBeaker' },
            text: localize(messages.runTestButton),
            disabled: !fileText.length || !selectedDeploymentName
        }
    ];

    const subtitle = (
        <>
            {localize(messages.subtitle)}
            &nbsp;
            <ExternalLink href="https://aka.ms/clu-test-model">{localize(messages.learnMore)}</ExternalLink>
        </>
    );

    return (
        <Page size="container-fluid" pageName="CluTestModelPage" title={localize(messages.title)}>
            <EditableTitle title={localize(messages.title)} subtitle={subtitle} />

            <Stack tokens={{ childrenGap: 15 }}>
                <LuisCommandBar items={commandBarItems} styles={commandBarStyles} />

                <DocumentInputStep
                    samplesList={[]}
                    hideFilesUploader
                    document={fileText}
                    documentLimit={500}
                    displayTitle={false}
                    onDocumentChange={setFileText}
                    featureName={'clu'}
                    selectedLanguage={selectedCulture}
                    displayAzureResourcePicker={false}
                    onLanguageChange={setSelectedCulture}
                    disableLanguageSelection={!isMultiLingual}
                    isTextRTL={isDocumentSwaggerRtl(selectedCulture as Language['code'])}
                    languagesList={(projectType === 'Conversation'
                        ? rootStore.cluConversationCultureStoreHouse
                        : rootStore.cluOrchestrationCultureStoreHouse
                    ).items.map(l => ({ code: l.code, name: l.name }))}
                    onRenderOptions={defaultRenderer => (
                        <Stack horizontal={isLargeOrGreater} verticalAlign="end" tokens={{ childrenGap: isMultiLingual ? 10 : 0 }}>
                            {defaultRenderer()}
                            <LuisDropdown
                                aria-required
                                selectedKey={selectedDeploymentName}
                                label={localize(messages.deploymentNameLabel)}
                                disabled={!deploymentsStoreHouse.tracker.isCompleted}
                                isLoading={deploymentsStoreHouse.tracker.isRunning}
                                placeholder={localize(messages.deploymentNamePlaceholder)}
                                onChange={(_, op) => setSelectedDeploymentName(op.key as string)}
                                options={deploymentsStoreHouse.items.map(d => ({ key: d.deploymentName, text: d.deploymentName }))}
                                styles={mergeStyleSets(
                                    deploymentsDropdownStyles,
                                    !isLargeOrGreater ? { dropdown: { minWidth: '160px' } } : undefined
                                )}
                            />
                        </Stack>
                    )}
                    textFieldProps={{
                        autoAdjustHeight: true,
                        styles: { fieldGroup: { minHeight: 50 } },
                        label: localize(messages.selectDocumentTitle)
                    }}
                />

                <ResultsStep
                    displayTitle={false}
                    isLoading={tracker.isRunning}
                    featureName={'clu'}
                    childrenContainerStyles={resultsContainerStyles}
                    results={testResults?.originalJsonResult}
                    emptyStateMessage={localize(messages.resultsPlaceholder)}
                    emptyStateImageSrc={getAssetsUrl('assets/images/clu/cluTestModelEmptyState.svg')}>
                    <VisualizedCluResult
                        document={testedDocument}
                        testResult={testResults}
                        isLabelerRtl={isDocumentSwaggerRtl(selectedCulture as Language['code'])}
                        isLabelerWordTokenized={isDocumentSwaggerWordTokenized(selectedCulture as Language['code'])}
                    />
                </ResultsStep>
            </Stack>
        </Page>
    );
});
